import { HeadRoles, Languages, StaffRoles } from "@cs124/person";
import { Array, Boolean, Dictionary, Literal, Number, Partial, Record, String, Union } from "runtypes";
export const RequestHelp = Record({
  type: Literal("requesthelp"),
  description: String,
  language: String,
  withWhat: String
});
export const WithdrawRequest = Record({
  type: Literal("withdrawrequest")
}).And(
  Partial({
    reason: String
  })
);
export const ArriveLocation = Record({
  type: Literal("arrivelocation"),
  location: String
});
export const DepartLocation = Record({
  type: Literal("departlocation")
}).And(
  Partial({
    reason: String,
    backoffTime: Number
  })
);
export const StudentArriveLocation = Record({
  type: Literal("studentarrivelocation"),
  location: String
});
export const StudentDepartLocation = Record({
  type: Literal("studentdepartlocation")
});
export const PresentKind = Literal("request").Or(Literal("button")).Or(Literal("ignoring")).Or(Literal("close")).Or(Literal("connection")).Or(Literal("old"));
export const StaffPresent = Record({
  type: Literal("staffpresent"),
  present: Boolean,
  kind: PresentKind
}).And(
  Partial({
    reason: String,
    client: Boolean
  })
);
export const KillClient = Record({
  type: Literal("killclient"),
  client: String
});
export const VALID_PRIORITIES = [0, 1];
export const RequestPriorities = Literal(0).Or(Literal(1));
export const SetRequestPriority = Record({
  type: Literal("setpriority"),
  priority: RequestPriorities
});
export const ClientMessages = Union(
  RequestHelp,
  WithdrawRequest,
  ArriveLocation,
  DepartLocation,
  StaffPresent,
  KillClient,
  StudentArriveLocation,
  StudentDepartLocation,
  SetRequestPriority
);
export const HelpRequest = Record({
  location: String,
  name: String,
  email: String,
  description: String,
  language: String,
  withWhat: String,
  waitingTime: Number,
  open: Boolean,
  staff: Array(
    Record({
      email: String,
      arrived: String,
      role: StaffRoles
    })
  ),
  lastHelped: Number,
  helpNext: Number,
  isStaff: Boolean,
  created: String,
  active: Boolean,
  priority: RequestPriorities
}).And(
  Partial({
    students: Array(String),
    team: String,
    partner: Record({
      email: String
    }).And(
      Partial({
        name: String
      })
    ),
    staffArrived: Number
  })
);
export const StaffAction = Record({
  staff: String,
  student: String,
  arrived: Number,
  left: Number
});
export const StudentStatus = Record({
  position: Number,
  request: HelpRequest,
  staffActions: Array(StaffAction)
});
export const StatusUpdate = Record({
  type: Literal("status"),
  timestamp: String,
  presentStaff: Array(String),
  presentClients: Array(String),
  requests: Array(HelpRequest),
  staffActions: Array(StaffAction),
  staffLockouts: Dictionary(Number),
  staffLanguages: Dictionary(Languages),
  staffRoles: Dictionary(StaffRoles),
  busyStaff: Array(String),
  ignoringStaff: Array(String),
  idleStaff: Array(String),
  ignoredLanguages: Array(String),
  ignoredLocations: Array(String)
}).And(
  Partial({
    token: String,
    staffBusy: Boolean,
    student: StudentStatus,
    partner: StudentStatus,
    myRequest: HelpRequest,
    project: Partial({
      team: String,
      partner: Record({
        email: String
      }).And(
        Partial({
          name: String
        })
      )
    })
  })
);
export const ServerMessages = Union(StatusUpdate, KillClient);
export const ConnectionQuery = Record({
  client: String,
  semester: String,
  number: String,
  headerVersion: String
}).And(
  Partial({
    impersonating: String
  })
);
export const MAX_IDLE_TIME = 20 * 60 * 1e3;
export function updateStaffStatus(status, now, onBreak, modifyStatus = false) {
  const busyStaff = [
    ...new Set(
      status.requests.filter((request) => !request.open).map((request) => request.staff.map((s) => s.email)).flat()
    )
  ].sort();
  const ignoredRequests = status.requests.filter(
    (request) => !request.isStaff && request.open && request.helpNext < now.valueOf()
  );
  const ignoredLocations = [...new Set(ignoredRequests.map((request) => request.location))].sort();
  const ignoredLanguages = [...new Set(ignoredRequests.map((request) => request.language))].sort();
  const ignoringStaff = [
    ...new Set(
      status.presentStaff.filter((staff) => {
        if (ignoredLanguages.length === 0) {
          return false;
        }
        if (onBreak.includes(staff)) {
          return false;
        }
        if (busyStaff.includes(staff)) {
          return false;
        }
        if (HeadRoles.includes(status.staffRoles[staff])) {
          return false;
        }
        return ignoredLanguages.includes("either") || ignoredLanguages.includes(status.staffLanguages[staff]) || status.staffLanguages[staff] === "both";
      })
    )
  ].sort();
  const ignoringLanguages = [
    .../* @__PURE__ */ new Set([
      ...ignoringStaff.map((s) => {
        if (status.staffLanguages[s] === "both") {
          return ["java", "kotlin"];
        } else {
          return [status.staffLanguages[s]];
        }
      }).flat()
    ])
  ];
  const idleStaff = [...status.presentStaff].filter((s) => !busyStaff.includes(s) && !ignoringStaff.includes(s));
  if (modifyStatus) {
    status.busyStaff = busyStaff;
    status.ignoringStaff = ignoringStaff;
    status.idleStaff = idleStaff;
    status.ignoredLanguages = ignoringLanguages;
    status.ignoredLocations = ignoredLocations;
  }
  return { busyStaff, ignoringStaff, idleStaff, ignoredLanguages: ignoringLanguages, ignoredLocations };
}
export const requestsWithLanguage = (requests, language) => requests.filter((request) => request.language === "either" || language === "both" || request.language === language);
export const openRequests = (requests, now) => requests.filter((request) => request.helpNext < now && request.open);
export const studentRequests = (requests) => requests.filter((request) => !request.isStaff);
export const PRIORITY_BOOST_TIMEOUT = process.env.DEVELOPMENT ? 30 * 1e3 : 20 * 60 * 1e3;
//# sourceMappingURL=types.js.map
